import { Suspense, useContext, useEffect, useState } from "react"

import { DevToolsSidebar } from "devTools/DevToolsSidebar"
import { env } from "env"
import { useLogFormJSON, useLogInitialValues } from "hooks"
import { sendTealiumEvent } from "tealium"
import { type ClientData } from "types"

import { ApplicationDataParser } from "components/ApplicationDataParser"
import { LayoutProvider } from "components/LayoutProvider"

import { AuthContext } from "context/AuthContext"
import { ClientContext } from "context/ClientContext"
import { FormContextProvider } from "context/FormContext"
import { OptionalLabelManager } from "context/OptionalLabelProvider"
import { UserContext } from "context/UserContext"

import { lessThanOneMinuteAgo } from "utilities/helpers"
import { generateValidationSchema } from "utilities/schema"

export const Authenticated = () => {
  const { token } = useContext(AuthContext)
  const { user } = useContext(UserContext)
  const { client } = useContext(ClientContext)
  const [validationSchema, setValidationSchema] = useState<any>()
  const [clientData, setClientData] = useState<ClientData>()

  useEffect(() => {
    if (!env.DEV) {
      setClientData(client?.data ?? ({} as ClientData))
    } else {
      import("data/colleges").then(({ COLLEGES }) => {
        const activeCollege = COLLEGES.find(c => c.currentlyActive)!
        setClientData(activeCollege.data)
      })
    }
  }, [client])

  // Update placeholer title and favicon with client data
  useEffect(() => {
    if (clientData?.meta) {
      const pageTitle = document.querySelector("title")
      if (pageTitle && clientData.meta.title) {
        pageTitle.innerHTML = clientData.meta.title
      }

      const favicon = document.querySelector<HTMLLinkElement>('[rel="icon"]')
      if (favicon && clientData.meta.favicon) {
        favicon.href = clientData.meta.favicon
      }
    }
  }, [clientData])

  const { applicationData } = user ?? {}

  useLogFormJSON(clientData ?? ({} as ClientData))
  useLogInitialValues(applicationData ?? {})

  useEffect(() => {
    if (client && clientData && !validationSchema) {
      const validations = generateValidationSchema(clientData)
      setValidationSchema(validations)
    }
    if (client && validationSchema) {
      if (user && lessThanOneMinuteAgo(user.createdAt)) {
        sendTealiumEvent("app_start", {
          type: "link",
          email_address: user.email,
        })
      }
    }
  }, [client, clientData, validationSchema, user])

  if (!user || !token || !clientData) {
    return null
  }

  return (
    <FormContextProvider
      validationSchema={validationSchema}
      user={user}
      token={token}
    >
      {env.DEV && DevToolsSidebar && (
        <Suspense fallback={null}>
          <DevToolsSidebar />
        </Suspense>
      )}
      <LayoutProvider clientData={clientData}>
        <OptionalLabelManager>
          <ApplicationDataParser {...clientData} />
        </OptionalLabelManager>
      </LayoutProvider>
    </FormContextProvider>
  )
}
